import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home,
  ShoppingCart,
  History,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  Users,
  Package,
  CreditCard,
  BarChart3,
  FileText,
  Wallet,
  Phone,
  Zap,
  Shield,
  Eye,
  Clock,
  BookOpen,
  PieChart,
  Bell,
  MoreHorizontal,
  Tag,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import NotificationDropdown from './NotificationDropdown';
import { TransactionService } from '../../services/transactionService';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [balanceDetailsLoading, setBalanceDetailsLoading] = useState(false);
  const [adminOpenSections, setAdminOpenSections] = useState({
    accounts: true,
    cards: false,
    topup: false
  });
  const [balanceDetails, setBalanceDetails] = useState({
    credit: 0,
    remainingBalance: 0
  });
  const { currentUser, userProfile, logout, isAdmin, isOperator } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const isAdminView = isAdmin();
  const isOperatorView = isOperator();
  const isResellerView = !isAdminView && !isOperatorView;

  // Détecter les changements de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
    setUserMenuOpen(false);
    setShowBalanceModal(false);

    // Force the viewport back to the top on every page navigation in the SPA.
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, location.search]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const formatBalance = (amount) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0
    }).format(amount || 0).replace('DZD', 'DA');
  };

  const openBalanceModal = async () => {
    if (isAdminView || isOperatorView || !currentUser?.uid) {
      return;
    }

    setShowBalanceModal(true);
    setBalanceDetailsLoading(true);

    const storedCredit = Number(userProfile?.creditBalance);
    const safeStoredCredit = Number.isFinite(storedCredit) ? Math.max(0, storedCredit) : 0;
    const remainingBalance = Number(userProfile?.balance || 0);

    try {
      const derivedCredit = await TransactionService.getDerivedUserCreditBalance(currentUser.uid, { includeLegacy: true });
      const netCredit = TransactionService.resolveEffectiveCreditBalance(userProfile, derivedCredit, { allowBalanceFallback: true });

      setBalanceDetails({
        credit: Math.max(0, netCredit),
        remainingBalance
      });
    } catch (error) {
      console.error('Erreur lors du chargement du détail du solde:', error);
      setBalanceDetails({
        credit: TransactionService.resolveEffectiveCreditBalance(userProfile, safeStoredCredit, { allowBalanceFallback: true }),
        remainingBalance
      });
    } finally {
      setBalanceDetailsLoading(false);
    }
  };

  const closeBalanceModal = () => {
    setShowBalanceModal(false);
  };

  useEffect(() => {
    if (!isResellerView) {
      return undefined;
    }

    const handleOpenBalanceDetails = () => {
      openBalanceModal();
    };

    window.addEventListener('open-balance-details', handleOpenBalanceDetails);
    return () => {
      window.removeEventListener('open-balance-details', handleOpenBalanceDetails);
    };
  }, [isResellerView, currentUser?.uid, userProfile?.balance]);

  // Navigation items pour les revendeurs
  const resellerNavItems = {
    cartes: [
      { name: 'Voir les cartes', href: '/catalog', icon: Eye },
      { name: 'Mes cartes et opérations', href: '/card-history', icon: CreditCard },
    ],
    topup: [
      { name: 'Topup', href: '/topup', icon: Zap },
    ],
    operations: [
      { name: 'Toutes les opérations', href: '/operations', icon: FileText },
    ]
  };

  // Navigation items pour les admins
  const adminNavItems = useMemo(() => ({
    accounts: {
      label: 'Gestion des comptes',
      icon: Users,
      items: [
        { name: 'Tableau de bord', href: '/admin', icon: Home },
        { name: 'Gestion des comptes', href: '/admin/users', icon: Users },
        { name: 'Transactions admin', href: '/admin/transactions', icon: Wallet },
        { name: 'Roles et permissions', href: '/admin/roles', icon: Shield }
      ]
    },
    cards: {
      label: 'Gestion des cartes',
      icon: CreditCard,
      items: [
        { name: 'Produits cartes', href: '/admin/products', icon: Package },
        { name: 'Categories cartes', href: '/admin/categories', icon: Tag }
      ]
    },
    topup: {
      label: 'Gestion des Topup',
      icon: Zap,
      items: [
        { name: 'Categories Topup', href: '/admin/topup/categories', icon: Tag },
        { name: 'Services Topup', href: '/admin/topup/services', icon: Zap },
        { name: 'Commandes Topup', href: '/admin/topup/orders', icon: FileText },
        { name: 'Templates Topup', href: '/admin/topup/templates', icon: Settings }
      ]
    }
  }), []);

  const operatorNavItems = useMemo(() => ({
    topup: {
      label: 'Back Office Topup',
      icon: Zap,
      items: [
        { name: 'Commandes Topup', href: '/operator/topup-orders', icon: FileText }
      ]
    }
  }), []);

  const resellerMobileNavItems = [
    { name: 'Accueil', href: '/dashboard', icon: Home },
    { name: 'Catalogue', href: '/catalog', icon: ShoppingCart },
    { name: 'Topup', href: '/topup', icon: Zap },
    { name: 'Cartes', href: '/card-history', icon: CreditCard },
    { name: 'Plus', action: () => setSidebarOpen(true), icon: MoreHorizontal }
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isAdminSectionActive = useCallback((sectionKey) => {
    const section = adminNavItems[sectionKey];
    if (!section) {
      return false;
    }

    return section.items.some((item) => isActive(item.href));
  }, [adminNavItems, location.pathname]);

  useEffect(() => {
    if (!isAdminView) {
      return;
    }

    setAdminOpenSections((current) => {
      const nextState = { ...current };
      Object.keys(adminNavItems).forEach((sectionKey) => {
        if (isAdminSectionActive(sectionKey)) {
          nextState[sectionKey] = true;
        }
      });
      return nextState;
    });
  }, [adminNavItems, isAdminView, isAdminSectionActive, location.pathname]);

  const toggleAdminSection = (sectionKey) => {
    setAdminOpenSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey]
    }));
  };

  const mobileHeaderHasBalance = !isLargeScreen && isResellerView && userProfile;

  return (
    <div className="min-h-screen overflow-x-hidden bg-gray-50">
      {/* Navbar */}
      <nav className="fixed top-0 z-[9999] w-full overflow-x-hidden border-b border-gray-200 bg-white shadow-sm" style={{
         left: isLargeScreen ? 'calc(288px + 25px)' : 0,
         right: 0,
         width: isLargeScreen ? 'calc(100% - 288px - 25px)' : '100%'
       }}>
        <div className="px-3 sm:px-6 lg:px-8" style={{width: '100%'}}>
          <div className={`flex justify-between gap-2 ${isLargeScreen ? 'h-16' : 'min-h-16 py-2'}`} style={{width: '100%'}}>
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              {/* Menu button pour mobile */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
              >
                {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              
              {mobileHeaderHasBalance && (
                <div className="flex min-w-0 items-center lg:hidden">
                  <button
                    type="button"
                    onClick={openBalanceModal}
                    className="flex min-w-0 max-w-[185px] items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 transition hover:bg-emerald-100"
                  >
                    <Wallet className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    <span className="truncate text-sm font-medium text-gray-600">Solde :</span>
                    <span className="truncate text-sm font-bold text-emerald-600">
                      {userProfile.balance?.toFixed(1) || '745.0'} DA
                    </span>
                  </button>
                </div>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2 sm:gap-4">
              {/* Notifications métier */}
              <NotificationDropdown />
              
              
              {/* User Avatar with Dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-1 p-1.5 sm:gap-2 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                >
                  <div className="h-9 w-9 sm:h-10 sm:w-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {userProfile?.firstName?.charAt(0) || 'D'}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-medium text-gray-900">
                      {userProfile?.firstName} {userProfile?.lastName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {isAdminView ? 'Administrateur' : isOperatorView ? 'Operateur' : 'Revendeur'}
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {userMenuOpen && (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                      onClick={() => setUserMenuOpen(false)}
                      aria-label="Fermer le menu profil"
                    />
                    <div className="fixed right-3 top-16 z-[10000] w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg sm:right-6 lg:right-8">
                      <div className="border-b border-gray-100 px-4 py-2">
                        <div className="text-sm font-medium text-gray-900">
                          {userProfile?.firstName} {userProfile?.lastName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {userProfile?.email}
                        </div>
                      </div>
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4 mr-3" />
                        Mon profil
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4 mr-3" />
                        Paramètres
                      </Link>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Se déconnecter
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

        </div>
      </nav>

      <div className="flex overflow-x-hidden">
        {/* Sidebar */}
        <div className={`fixed top-16 bottom-0 left-0 z-40 w-72 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className="px-6 py-4 border-b border-gray-100">
              <Link to="/dashboard" onClick={() => setSidebarOpen(false)} className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                <div className="h-10 w-10 rounded-xl overflow-hidden flex items-center justify-center">
                  <img 
                    src="/logo.png" 
                    alt="VoucherPro Logo" 
                    className="h-10 w-10 object-contain"
                  />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">VoucherPro</h2>
                  <p className="text-sm text-gray-500">{isAdminView ? 'Admin' : isOperatorView ? 'Operateur' : 'Revendeur'}</p>
                </div>
              </Link>
            </div>
            
            {/* Balance Display for Resellers - Desktop only */}
            {isLargeScreen && isResellerView && userProfile && (
              <div className="px-6 py-3 border-b border-gray-100">
                <button
                  type="button"
                  onClick={openBalanceModal}
                  className="flex w-full items-center space-x-2 rounded-lg bg-green-50 px-3 py-2 text-left transition hover:bg-green-100"
                >
                  <Wallet className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-gray-600">Solde :</span>
                  <span className="text-sm font-semibold text-green-600">
                    {userProfile.balance?.toFixed(1) || '745.0'} DA
                  </span>
                </button>
              </div>
            )}
            
            {/* Navigation */}
            <nav className="flex-1 px-6 py-6 space-y-6">
              {/* Navigation pour les revendeurs */}
              {isResellerView && (
                <>
                  {/* Section CARTES */}
                   <div>
                     <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                       CARTES
                     </h3>
                     <div className="space-y-2">
                       {resellerNavItems.cartes.map((item) => {
                         const Icon = item.icon;
                         return (
                           <Link
                             key={item.name}
                             to={item.href}
                             onClick={() => setSidebarOpen(false)}
                             className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                               isActive(item.href)
                                 ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                                 : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                             }`}
                           >
                             <Icon className={`mr-4 h-5 w-5 ${
                               isActive(item.href) ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                             }`} />
                             <span className="font-medium">{item.name}</span>
                           </Link>
                         );
                       })}
                     </div>
                   </div>

                  {/* Section FREE FIRE */}
                   <div>
                     <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                       TOPUP
                     </h3>
                     <div className="space-y-2">
                       {resellerNavItems.topup.map((item) => {
                         const Icon = item.icon;
                         return (
                           <Link
                             key={item.name}
                             to={item.href}
                             onClick={() => setSidebarOpen(false)}
                             className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                               isActive(item.href)
                                 ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                                 : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                             }`}
                           >
                             <Icon className={`mr-4 h-5 w-5 ${
                               isActive(item.href) ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                             }`} />
                             <span className="font-medium">{item.name}</span>
                           </Link>
                         );
                       })}
                     </div>
                   </div>

                  {/* Section TOUTES LES OPÃ‰RATIONS */}
                   <div>
                     <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                       TOUTES LES OPÉRATIONS
                     </h3>
                     <div className="space-y-2">
                       {resellerNavItems.operations.map((item) => {
                         const Icon = item.icon;
                         return (
                           <Link
                         key={item.name}
                         to={item.href}
                         onClick={() => setSidebarOpen(false)}
                         className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                           isActive(item.href)
                             ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                             : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                         }`}
                       >
                             <Icon className={`mr-4 h-5 w-5 ${
                               isActive(item.href) ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                             }`} />
                             <span className="font-medium">{item.name}</span>
                           </Link>
                         );
                       })}
                     </div>
                   </div>

                </>
              )}

              {/* Navigation pour les administrateurs */}
              {isAdminView && (
                <div>
                  <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                    ADMINISTRATION
                  </h3>

                  <div className="space-y-3">
                    {Object.entries(adminNavItems).map(([sectionKey, section]) => {
                      const SectionIcon = section.icon;
                      const isOpen = adminOpenSections[sectionKey];
                      const sectionActive = isAdminSectionActive(sectionKey);

                      return (
                        <div key={sectionKey} className="rounded-2xl border border-gray-100 bg-gray-50/80">
                          <button
                            type="button"
                            onClick={() => toggleAdminSection(sectionKey)}
                            className={`flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left transition ${
                              sectionActive ? 'bg-blue-50 text-blue-700' : 'text-gray-800 hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`rounded-xl p-2 ${sectionActive ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-500'}`}>
                                <SectionIcon className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold">{section.label}</p>
                                <p className="text-xs text-gray-500">{section.items.length} sous-menu(x)</p>
                              </div>
                            </div>

                            {isOpen ? (
                              <ChevronDown className={`h-4 w-4 ${sectionActive ? 'text-blue-600' : 'text-gray-400'}`} />
                            ) : (
                              <ChevronRight className={`h-4 w-4 ${sectionActive ? 'text-blue-600' : 'text-gray-400'}`} />
                            )}
                          </button>

                          {isOpen && (
                            <div className="space-y-1 px-3 pb-3">
                              {section.items.map((item) => {
                                const Icon = item.icon;
                                const active = isActive(item.href);

                                return (
                                  <Link
                                    key={item.name}
                                    to={item.href}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`group flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                                      active
                                        ? 'bg-white text-blue-700 shadow-sm border border-blue-100'
                                        : 'text-gray-700 hover:bg-white hover:text-gray-900'
                                    }`}
                                  >
                                    <Icon className={`mr-3 h-4 w-4 ${
                                      active ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                                    }`} />
                                    <span className="font-medium">{item.name}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {isOperatorView && (
                <div>
                  <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                    OPERATEUR
                  </h3>

                  <div className="space-y-3">
                    {Object.entries(operatorNavItems).map(([sectionKey, section]) => {
                      const SectionIcon = section.icon;

                      return (
                        <div key={sectionKey} className="rounded-2xl border border-gray-100 bg-gray-50/80">
                          <div className="flex items-center gap-3 rounded-2xl px-4 py-3 text-left text-gray-800">
                            <div className="rounded-xl bg-white p-2 text-gray-500">
                              <SectionIcon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold">{section.label}</p>
                              <p className="text-xs text-gray-500">{section.items.length} ecran(x)</p>
                            </div>
                          </div>

                          <div className="space-y-1 px-3 pb-3">
                            {section.items.map((item) => {
                              const Icon = item.icon;
                              const active = isActive(item.href);

                              return (
                                <Link
                                  key={item.name}
                                  to={item.href}
                                  onClick={() => setSidebarOpen(false)}
                                  className={`group flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                                    active
                                      ? 'bg-white text-blue-700 shadow-sm border border-blue-100'
                                      : 'text-gray-700 hover:bg-white hover:text-gray-900'
                                  }`}
                                >
                                  <Icon className={`mr-3 h-4 w-4 ${
                                    active ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                                  }`} />
                                  <span className="font-medium">{item.name}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </nav>
            
            {/* Espace pour étendre la sidebar jusqu'en bas */}
            <div className="flex-1"></div>
          </div>
        </div>

        {/* Overlay pour mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 z-30 bg-gray-600 bg-opacity-50 lg:hidden" 
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <div className="flex-1 overflow-x-hidden pt-16 lg:ml-0">
          <main className={`p-3 sm:p-4 lg:p-6 ${isResellerView ? 'pb-24 lg:pb-6' : ''}`}>
            {children}
          </main>
        </div>
      </div>

      {!isLargeScreen && isResellerView && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[9998] overflow-x-hidden border-t border-gray-200 bg-white shadow-[0_-10px_30px_rgba(15,23,42,0.08)]"
          style={{
            paddingBottom: 'max(env(safe-area-inset-bottom), 0px)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden'
          }}
        >
          <div className="grid grid-cols-5">
            {resellerMobileNavItems.map((item) => {
              const Icon = item.icon;
              const active = item.href ? isActive(item.href) : false;

              if (item.href) {
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex min-w-0 flex-col items-center justify-center gap-1 px-2 py-2.5 text-[11px] font-medium ${
                      active ? 'text-primary-600' : 'text-gray-500'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
                    <span className="truncate">{item.name}</span>
                  </Link>
                );
              }

              return (
                <button
                  key={item.name}
                  type="button"
                  onClick={item.action}
                  className="flex min-w-0 flex-col items-center justify-center gap-1 px-2 py-2.5 text-[11px] font-medium text-gray-500"
                >
                  <Icon className="h-5 w-5 text-gray-400" />
                  <span className="truncate">{item.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showBalanceModal && isResellerView && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4">
          <button
            type="button"
            className="absolute inset-0"
            onClick={closeBalanceModal}
            aria-label="Fermer"
          />

          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Détail du solde</h3>
                <p className="text-xs text-gray-500">Bonus non inclus</p>
              </div>
              <button
                type="button"
                onClick={closeBalanceModal}
                className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                aria-label="Fermer le détail du solde"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              {balanceDetailsLoading ? (
                <div className="space-y-3">
                  <div className="h-24 animate-pulse rounded-2xl bg-gray-100" />
                  <div className="h-24 animate-pulse rounded-2xl bg-gray-100" />
                </div>
              ) : (
                <>
                  <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-white/80 p-2 text-blue-600">
                        <CreditCard className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Son credit</p>
                        <p className="mt-1 text-2xl font-bold text-blue-700">
                          {formatBalance(balanceDetails.credit)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-white/80 p-2 text-emerald-600">
                        <Wallet className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Solde restant</p>
                        <p className="mt-1 text-2xl font-bold text-emerald-700">
                          {formatBalance(balanceDetails.remainingBalance)}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
